import {
  IconChartGraph2,
  IconCoinDollar2,
  IconCoinEuro,
  IconFlagMoneyIDR,
} from "@aksara-ui/icons";
import {
  Badge,
  Box,
  Card,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogProps,
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  theme,
} from "@aksara-ui/react";
import TableBodyRowsSkeleton from "components/TableBodyRowSkeleton";
import moment from "moment";
import { transparentize } from "polished";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { setLoadingModal } from "redux/actions";
import MemberService from "service/Member";
import { showMessage } from "utilities/helper";
import { toCapitilizeCase } from "utilities/helper/toCapitalizeCase";

interface Props {
  memberId: string;
}

const MemberDetailModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  memberId,
}) => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ maxWidth: 576 });

  const { members } = useSelector((state: any) => state.memberReducer);
  const { isLoadingModal } = useSelector((state: any) => state.globalReducer);

  const [expPoint, setExpPoint] = useState(0);
  const [ultraPoint, setUltraPoint] = useState(0);
  const [tier, setTier] = useState("");
  const [tierColor, setTierColor] = useState("");
  const [spentCost, setSpentCost] = useState(0);

  const [datas, setDatas] = useState<any>([]);

  const handleCloseModal = () => {
    onClose && onClose();
    setExpPoint(0);
    setUltraPoint(0);
    setTier("");
    setTierColor("");
    setSpentCost(0);
    setDatas([]);
  };

  const getAllDatas = async (memberId: number) => {
    dispatch(setLoadingModal(true));
    try {
      let data = [];
      data = await MemberService.getMemberPointActivities(memberId);
      dispatch(setLoadingModal(false));
      setDatas(data.member_point_activities);
    } catch (err: any) {
      dispatch(setLoadingModal(false));
      if (err.request.status !== 401) {
        console.log("error :", err);
        showMessage(
          !err.response
            ? err.message
            : !!err.response?.data?.error
            ? err.response?.data?.error
            : err.response?.data?.message,
          "error"
        );
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      const memberDetail = members.find(
        (member: any) => member.id === parseInt(memberId)
      );

      setExpPoint(
        memberDetail.total_xp_point ? parseInt(memberDetail.total_xp_point) : 0
      );
      setUltraPoint(
        memberDetail.total_reward_point
          ? parseInt(memberDetail.total_reward_point)
          : 0
      );
      setTier(
        memberDetail.tier_name
          ? toCapitilizeCase(memberDetail.tier_name)
          : "Silver"
      );
      setTierColor(
        memberDetail.tier_name === "silver"
          ? "#b2bec3"
          : memberDetail.tier_name === "gold"
          ? "#fdcb6e"
          : memberDetail.tier_name === "platinum"
          ? "#636e72"
          : "#f3a960"
      );
      setSpentCost(
        memberDetail.loyalty_cost ? parseInt(memberDetail.loyalty_cost) : 0
      );

      getAllDatas(parseInt(memberId));
    }
  }, [dispatch, isOpen, members, memberId]);

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
      maxWidth={"1000px"}
    >
      <DialogHeader>
        <Text
          fontWeight={500}
          color={theme.colors.grey07}
          fontSize={[400, 500]}
        >
          Member Detail
        </Text>
      </DialogHeader>
      <DialogContent>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          flexDirection={isMobile ? "column" : "row"}
          marginBottom={40}
          style={{ gap: 20 }}
        >
          <Card
            elevation={2}
            width={isMobile ? "100%" : "30%"}
            backgroundColor={transparentize(0.7, theme.colors.grey01)}
          >
            <Box display={"flex"} flexDirection={"row"}>
              <Box backgroundColor={theme.colors.blue03} width={20}></Box>
              <Box
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                paddingTop={10}
                padding={20}
                width={"100%"}
              >
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  textAlign={"left"}
                  width={"100%"}
                  style={{ gap: 10 }}
                >
                  <Text
                    fontWeight={700}
                    color={theme.colors.blue04}
                    fontSize={[400, 500]}
                  >
                    {ultraPoint.toLocaleString()}
                  </Text>
                  <Text
                    color={theme.colors.grey06}
                    fontSize={100}
                    lineHeight={1.2}
                  >
                    Ancol Points
                  </Text>
                </Box>
                <IconCoinDollar2 fill={theme.colors.blue04} size={40} />
              </Box>
            </Box>
          </Card>
          <Card
            elevation={2}
            width={isMobile ? "100%" : "30%"}
            backgroundColor={transparentize(0.7, theme.colors.grey01)}
          >
            <Box display={"flex"} flexDirection={"row"}>
              <Box backgroundColor={theme.colors.yellow03} width={20}></Box>
              <Box
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                paddingTop={10}
                padding={20}
                width={"100%"}
              >
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  textAlign={"left"}
                  width={"100%"}
                  style={{ gap: 10 }}
                >
                  <Text
                    fontWeight={700}
                    color={theme.colors.yellow04}
                    fontSize={[400, 500]}
                  >
                    {expPoint.toLocaleString()}
                  </Text>
                  <Text
                    color={theme.colors.grey06}
                    fontSize={100}
                    lineHeight={1.2}
                  >
                    Exp Point
                  </Text>
                </Box>
                <IconCoinEuro fill={theme.colors.yellow04} size={40} />
              </Box>
            </Box>
          </Card>
          <Card
            elevation={2}
            width={isMobile ? "100%" : "30%"}
            backgroundColor={transparentize(0.7, theme.colors.grey01)}
          >
            <Box display={"flex"} flexDirection={"row"}>
              <Box backgroundColor={tierColor} width={20}></Box>
              <Box
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                paddingTop={10}
                padding={20}
                width={"100%"}
              >
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  textAlign={"left"}
                  width={"100%"}
                  style={{ gap: 10 }}
                >
                  <Text
                    fontWeight={700}
                    color={tierColor}
                    fontSize={[400, 500]}
                  >
                    {tier}
                  </Text>
                  <Text color={tierColor} fontSize={100} lineHeight={1.2}>
                    Tier
                  </Text>
                </Box>
                <IconChartGraph2 fill={tierColor} size={40} />
              </Box>
            </Box>
          </Card>
          <Card
            elevation={2}
            width={isMobile ? "100%" : "30%"}
            backgroundColor={transparentize(0.7, theme.colors.grey01)}
          >
            <Box display={"flex"} flexDirection={"row"}>
              <Box backgroundColor={theme.colors.red03} width={20}></Box>
              <Box
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                paddingTop={10}
                padding={20}
                width={"100%"}
              >
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  textAlign={"left"}
                  width={"100%"}
                  style={{ gap: 10 }}
                >
                  <Text
                    fontWeight={700}
                    color={theme.colors.red04}
                    fontSize={[400, 500]}
                  >
                    {spentCost.toLocaleString()}
                  </Text>
                  <Text
                    color={theme.colors.grey06}
                    fontSize={100}
                    lineHeight={1.2}
                  >
                    Spent Cost
                  </Text>
                </Box>
                <IconFlagMoneyIDR fill={theme.colors.red04} size={40} />
              </Box>
            </Box>
          </Card>
        </Box>

        <Box marginBottom={10}>
          <Text fontWeight={500} color={theme.colors.grey07} fontSize={300}>
            Point Activity
          </Text>
        </Box>

        <Box marginBottom={40}>
          <Card maxHeight={"100%"} overflow={"auto"} elevation={2}>
            <Table>
              <TableHead position={"sticky"} top={0} backgroundColor={"white"}>
                <TableHeadRow>
                  <TableHeadCell>Source</TableHeadCell>
                  <TableHeadCell>Description</TableHeadCell>
                  <TableHeadCell minWidth={150}>Type</TableHeadCell>
                  <TableHeadCell>Point</TableHeadCell>
                  <TableHeadCell>Date/Time</TableHeadCell>
                </TableHeadRow>
              </TableHead>
              <TableBody>
                {isLoadingModal ? (
                  <TableBodyRowsSkeleton rowsNumber={10} columnsNumber={5} />
                ) : !datas.length ? (
                  <TableBodyRow>
                    <TableBodyCell colSpan={5} textAlign={"center"}>
                      No reward available
                    </TableBodyCell>
                  </TableBodyRow>
                ) : (
                  datas.map((data: any) => {
                    return (
                      <TableBodyRow key={data.id}>
                        <TableBodyCell>{data.source}</TableBodyCell>
                        <TableBodyCell>{data.description}</TableBodyCell>
                        <TableBodyCell>
                          <Badge
                            size="md"
                            variant={
                              data.point_type === "0" ? "warning" : "info"
                            }
                          >
                            {data.point_type === "0"
                              ? "Exp Point"
                              : "Ancol Point"}
                          </Badge>
                        </TableBodyCell>
                        <TableBodyCell>
                          <Badge
                            size="md"
                            variant={
                              data.is_increase_point ? "success" : "critical"
                            }
                          >
                            {data.is_increase_point
                              ? "+" + data.point
                              : "-" + data.point}
                          </Badge>
                        </TableBodyCell>
                        <TableBodyCell whiteSpace={"nowrap"}>
                          <Badge size="md" variant="info">
                            {moment(data.created_at).format("DD MMMM YYYY")}
                          </Badge>
                        </TableBodyCell>
                      </TableBodyRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Card>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MemberDetailModal;
